import { useState, useEffect, useRef } from "react";
import { supabase, BRAND, MUTED, unreadBadge, text, btn, iconBtn, SHADOW_POP, NEUTRAL_BG, WHITE, INK, LINE } from "./constants";
import { CloseButton } from "./shared";
import { useIsMobile } from "./lib/responsive";
import { ChevronDown, ChevronUp } from "lucide-react";
import { authFetch } from "./lib/ai";

const MessageBubble = ({ msg, isOwn, lang }) => {
  const ts = new Date(msg.created_at);
  const timeStr = ts.toLocaleString(lang === "es" ? "es-ES" : "en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" });
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: isOwn ? "flex-end" : "flex-start", gap: 4 }}>
      <div style={{
        maxWidth: "80%",
        background: isOwn ? BRAND : NEUTRAL_BG,
        color: isOwn ? WHITE : INK,
        padding: "10px 14px",
        borderRadius: 14,
        borderTopRightRadius: isOwn ? 4 : 14,
        borderTopLeftRadius: isOwn ? 14 : 4,
        fontSize: 13,
        lineHeight: 1.55,
        wordWrap: "break-word",
        whiteSpace: "pre-wrap",
      }}>
        {msg.message}
      </div>
      <div style={{ fontSize: 11, color: MUTED, padding: "0 4px" }}>{timeStr}</div>
    </div>
  );
};

const ChatDrawer = ({ open, onClose, clientId, userEmail, isAdmin, lang }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const scrollRef = useRef(null);
  const isMobile = useIsMobile();
  // Móvil: la hoja ocupa la parte VISIBLE de la pantalla (visualViewport), así
  // el campo de escribir queda encima del teclado en vez de debajo.
  const [viewport, setViewport] = useState(null); // { top, height } | null
  // Escritorio (15/10): ventana flotante abajo a la derecha, como la de
  // LinkedIn. No bloquea la página y se minimiza a su barra de título; los
  // mensajes que llegan minimizada se cuentan en esa barra.
  const [minimized, setMinimized] = useState(false);
  const [seenCount, setSeenCount] = useState(0);
  useEffect(() => { if (!open) setMinimized(false); }, [open]);

  useEffect(() => {
    if (!open || !isMobile || typeof window === "undefined" || !window.visualViewport) { setViewport(null); return; }
    const vv = window.visualViewport;
    const sync = () => setViewport({ top: vv.offsetTop, height: vv.height });
    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => { vv.removeEventListener("resize", sync); vv.removeEventListener("scroll", sync); };
  }, [open, isMobile]);

  const loadMessages = async () => {
    if (!clientId) return;
    const { data } = await supabase
      .from("recruiter_chat_messages")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: true });
    setMessages(data || []);
    setLoadingInitial(false);
  };

  const markAsRead = async () => {
    if (!clientId) return;
    const field = isAdmin ? "read_by_admin" : "read_by_client";
    await supabase
      .from("recruiter_chat_messages")
      .update({ [field]: true })
      .eq("client_id", clientId)
      .eq(field, false);
  };

  // Initial load + polling every 30s + mark as read on open
  useEffect(() => {
    if (!open || !clientId) return;
    setLoadingInitial(true);
    loadMessages();
    markAsRead();
    const interval = setInterval(loadMessages, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  // Auto-scroll to bottom when messages change (y al desplegar la ventana)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, viewport, minimized]);

  // Mensajes del otro lado: los que llegan con la ventana minimizada se cuentan
  // en la barra; al desplegarla se dan por leídos.
  const otherRole = isAdmin ? "client" : "admin";
  const theirs = messages.filter(m => m.sender_role === otherRole).length;
  const newWhileMin = minimized ? Math.max(0, theirs - seenCount) : 0;
  const toggleMin = () => {
    if (minimized) { markAsRead(); setMinimized(false); }
    else { setSeenCount(theirs); setMinimized(true); }
  };

  const sendMessage = async () => {
    const text = inputText.trim();
    if (!text || !clientId || sending) return;
    setSending(true);
    const payload = {
      client_id: clientId,
      sender_role: isAdmin ? "admin" : "client",
      sender_email: userEmail || "",
      message: text,
      read_by_admin: isAdmin,
      read_by_client: !isAdmin,
    };
    const { error } = await supabase.from("recruiter_chat_messages").insert(payload);
    setSending(false);
    if (error) { console.error("Send message error:", error); return; }
    setInputText("");
    await loadMessages();
    // Notify admin by email if client sent
    if (!isAdmin) {
      authFetch("/api/send-email", {
        body: JSON.stringify({
          type: "client_chat_message",
          clientId,
          senderEmail: userEmail,
          message: text,
        })
      }).catch(e => console.error("Email notify error:", e));
    }
  };

  const handleKeyDown = (e) => {
    // En el móvil, Enter hace salto de línea (en el teclado del teléfono no hay
    // Mayúsculas+Enter): se envía con el botón, como en el portal (24/10)
    if (e.key === "Enter" && !e.shiftKey && !isMobile) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Ventana: a pantalla completa en el móvil; en escritorio flotante abajo
          a la derecha, sin tapar ni bloquear el resto de la página */}
      <div role="dialog" aria-label={lang === "es" ? "Chat con el recruiter" : "Recruiter chat"} style={isMobile ? {
        // Hoja a pantalla completa en el móvil (por encima de la barra inferior)
        position: "fixed", left: 0, right: 0, top: viewport ? viewport.top : 0,
        height: viewport ? viewport.height : "100dvh", width: "100%",
        background: WHITE, display: "flex", flexDirection: "column", zIndex: 1000,
        paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box",
      } : {
        position: "fixed", right: 24, bottom: 0, width: 380, maxWidth: "calc(100vw - 48px)",
        height: minimized ? "auto" : "min(560px, calc(100vh - 96px))",
        background: WHITE, border: `1.5px solid ${LINE}`, borderBottom: "none", borderRadius: "12px 12px 0 0",
        boxShadow: SHADOW_POP, zIndex: 900,
        display: "flex", flexDirection: "column", overflow: "hidden",
        animation: "chatUp 0.18s ease-out",
      }}>
        {/* Header */}
        <div onClick={isMobile ? undefined : toggleMin}
          style={{ padding: isMobile ? "10px 12px 10px 16px" : "12px 10px 12px 16px", borderBottom: minimized ? "none" : `1.5px solid ${LINE}`, gap: isMobile ? 12 : 6, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, cursor: isMobile ? "default" : "pointer" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ ...text.cardTitle, display: "flex", alignItems: "center", gap: 8 }}>
              {lang === "es" ? "Chat con el recruiter" : "Recruiter chat"}
              {newWhileMin > 0 && <span style={unreadBadge}>{newWhileMin}</span>}
            </div>
            {!minimized && (
              <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                {lang === "es" ? "Línea directa con nuestro equipo" : "Direct line with our team"}
              </div>
            )}
          </div>
          {!isMobile && (
            <button onClick={e => { e.stopPropagation(); toggleMin(); }} title={minimized ? (lang === "es" ? "Abrir" : "Open") : (lang === "es" ? "Minimizar" : "Minimise")}
              style={iconBtn}>
              {minimized ? <ChevronUp size={18} strokeWidth={1.75} /> : <ChevronDown size={18} strokeWidth={1.75} />}
            </button>
          )}
          <CloseButton onClick={e => { e.stopPropagation(); onClose(); }} label={lang === "es" ? "Cerrar" : "Close"} />
        </div>

        {!minimized && <>

        {/* Messages */}
        <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px", display: "flex", flexDirection: "column", gap: 14 }}>
          {loadingInitial && (
            <div style={{ textAlign: "center", ...text.body, color: MUTED, padding: 24 }}>
              {lang === "es" ? "Cargando..." : "Loading..."}
            </div>
          )}
          {!loadingInitial && messages.length === 0 && (
            <div style={{ textAlign: "center", ...text.body, color: MUTED, padding: 40 }}>
              {lang === "es"
                ? "Sin mensajes todavía. Escribe lo primero y el equipo te contesta cuanto antes."
                : "No messages yet. Start the conversation and the team will reply as soon as possible."}
            </div>
          )}
          {messages.map(msg => {
            const isOwn = (msg.sender_role === "admin") === isAdmin;
            return <MessageBubble key={msg.id} msg={msg} isOwn={isOwn} lang={lang} />;
          })}
        </div>

        {/* Input */}
        <div style={{ padding: isMobile ? "10px 12px calc(10px + env(safe-area-inset-bottom))" : "12px 14px 14px", borderTop: `1.5px solid ${LINE}`, display: "flex", gap: 8, alignItems: "flex-end", flexShrink: 0 }}>
          <textarea
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isMobile ? (lang === "es" ? "Escribe un mensaje..." : "Write a message...") : (lang === "es" ? "Escribe un mensaje... (Enter para enviar)" : "Write a message... (Enter to send)")}
            disabled={sending}
            rows={2}
            style={{
              flex: 1, padding: "10px 14px",
              border: `1.5px solid ${LINE}`, borderRadius: 10,
              fontSize: isMobile ? 16 : 13, fontFamily: "inherit",
              resize: "none", outline: "none",
              minHeight: 44, maxHeight: 140,
              boxSizing: "border-box",
            }}
            onFocus={e => e.target.style.borderColor = BRAND}
            onBlur={e => e.target.style.borderColor = LINE}
          />
          <button onClick={sendMessage} disabled={sending || !inputText.trim()}
            style={{
              ...(sending || !inputText.trim() ? btn.disabled : btn.primary),
              padding: "10px 20px",
              flexShrink: 0,
              ...(isMobile ? { minHeight: 44, fontSize: 15, padding: "10px 16px" } : null),
            }}>
            {sending ? "..." : (lang === "es" ? "Enviar" : "Send")}
          </button>
        </div>
        </>}
      </div>

      <style>{`@keyframes chatUp { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </>
  );
};

export default ChatDrawer;
